/**
 * SDF Sprite Generator (shared between Task and Note symbol layers)
 *
 * Generates Signed Distance Field (SDF) ImageData for composite marker sprites.
 * Task composite sprites combine a shape fill with an icon cutout (transparent
 * area); the cutout reveals the underlying border layer color (white/dark,
 * theme-aware). The note marker uses two stacked solid sprites instead — a
 * solid teardrop pin in the per-feature color plus a separate IconNote glyph
 * sprite tinted with a contrast color — so the glyph stays filled without an
 * outline layer underneath.
 *
 * All sprites use white fill on transparent background — only the alpha channel
 * matters for SDF mode.
 *
 * @module components/map/symbolSprites/spriteGenerator
 */

import { renderToStaticMarkup } from 'react-dom/server';
import { createElement } from 'react';
import { IconNote } from '@tabler/icons-react';
import { MarkerIcon, type MarkerIconType } from '@/types/MarkerIcon';
import { getTablerIcon } from '@/utils/markerIcons';

import { generateObstructionSprites } from './obstructionSprites';

// ===== SPRITE SIZE CONSTANTS =====

/** Canvas size for all sprites (shape + composite) */
export const SHAPE_SPRITE_SIZE = 64;

/** Rounded corner radius ratio for the square shape — multiplied by the actual
 *  canvas size at draw time so super-sampled sprites keep the same corner curve. */
const SQUARE_CORNER_RADIUS_RATIO = 1 / 16;

/** Padding ratio for shapes to prevent clipping at edges */
const SHAPE_PADDING_RATIO = 0.1;

/** SDF fill color - white for alpha-only rendering */
const SDF_FILL_COLOR = 'white';

/**
 * Icon cutout size inside the 64px composite sprite.
 * Matches DOM marker proportions: 16px icon in 36px marker → 16*(64/36) ≈ 28px.
 */
const COMPOSITE_ICON_SIZE = 28;

/** Note pin (teardrop) icon cutout size — slightly smaller than other shapes
 *  because the pin has a tail that consumes vertical space. */
const NOTE_COMPOSITE_ICON_SIZE = 22;

/**
 * Triangle icon vertical offset in pixels (baked into composite sprite).
 * Shifts icon toward the visual centroid (1/3 from base, not geometric center).
 */
const TRIANGLE_ICON_OFFSET_Y = 3;

/**
 * Tabler icon SVG stroke width used for cutout rendering.
 * Must match the stroke width used in DOM marker icons for visual consistency.
 */
const ICON_STROKE_WIDTH = 2;

// ===== SHAPE TYPE =====

type ShapeType = 'circle' | 'square' | 'triangle';

// ===== CANVAS HELPERS =====

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function createCanvas(
  width: number,
  height: number
): { canvas: HTMLCanvasElement | OffscreenCanvas; ctx: Ctx } {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get OffscreenCanvas 2D context');
    return { canvas, ctx };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get Canvas 2D context');
  return { canvas, ctx };
}

function extractImageData(ctx: Ctx, size: number): ImageData {
  return ctx.getImageData(0, 0, size, size);
}

// ===== SHAPE DRAWING =====

function drawCircle(ctx: Ctx, size: number): void {
  const padding = size * SHAPE_PADDING_RATIO;
  const radius = (size - padding * 2) / 2;
  const center = size / 2;

  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fillStyle = SDF_FILL_COLOR;
  ctx.fill();
}

function drawSquare(ctx: Ctx, size: number): void {
  const padding = size * SHAPE_PADDING_RATIO;
  const rectSize = size - padding * 2;
  const cornerRadius = size * SQUARE_CORNER_RADIUS_RATIO;

  ctx.beginPath();
  ctx.roundRect(padding, padding, rectSize, rectSize, cornerRadius);
  ctx.fillStyle = SDF_FILL_COLOR;
  ctx.fill();
}

function drawTriangle(ctx: Ctx, size: number): void {
  const padding = size * SHAPE_PADDING_RATIO;
  const drawableSize = size - padding * 2;
  const centerX = size / 2;

  const topY = padding;
  const bottomY = padding + drawableSize;
  const halfBase = drawableSize / 2;

  ctx.beginPath();
  ctx.moveTo(centerX, topY);
  ctx.lineTo(centerX + halfBase, bottomY);
  ctx.lineTo(centerX - halfBase, bottomY);
  ctx.closePath();
  ctx.fillStyle = SDF_FILL_COLOR;
  ctx.fill();
}

/**
 * Draws a teardrop / map-pin shape:
 *   ┌─────────┐   circular head occupying the upper ~70% of the canvas
 *   │  ◯ ◯   │   with a tapering tail anchored at the bottom-center pixel.
 *   │   ▽    │
 *
 * Anchored at the BOTTOM of the canvas so the layer's `icon-anchor: 'bottom'`
 * points the tail tip exactly at the geographic coordinate.
 */
function drawTeardropPin(ctx: Ctx, size: number): void {
  const padding = size * SHAPE_PADDING_RATIO;
  const centerX = size / 2;
  const headRadius = centerX - padding;
  const headCenterY = size / 2;
  const tailTipY = size;

  const dy = tailTipY - headCenterY;
  const tangentAngle = Math.asin(headRadius / dy);
  const rightTangent = tangentAngle;
  const leftTangent = Math.PI - tangentAngle;

  ctx.beginPath();
  ctx.arc(centerX, headCenterY, headRadius, rightTangent, leftTangent, true);
  ctx.lineTo(centerX, tailTipY);
  ctx.closePath();
  ctx.fillStyle = SDF_FILL_COLOR;
  ctx.fill();
}

const SHAPE_DRAW_FUNCTIONS: Record<
  ShapeType,
  (ctx: Ctx, size: number) => void
> = {
  circle: drawCircle,
  square: drawSquare,
  triangle: drawTriangle,
};

// ===== SVG HELPERS =====

function getIconSvgMarkup(iconKey: MarkerIconType, size: number): string {
  const IconComponent = getTablerIcon(iconKey);
  if (!IconComponent) throw new Error(`Unknown icon key: ${iconKey}`);

  return renderToStaticMarkup(
    createElement(IconComponent, {
      size,
      color: SDF_FILL_COLOR,
      stroke: ICON_STROKE_WIDTH,
    })
  );
}

function getNoteIconSvgMarkup(size: number): string {
  return renderToStaticMarkup(
    createElement(IconNote, {
      size,
      color: SDF_FILL_COLOR,
      stroke: ICON_STROKE_WIDTH,
    })
  );
}

function loadSvgAsImage(
  svgMarkup: string,
  size: number
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const encodedSvg = encodeURIComponent(svgMarkup);
    const image = new Image();
    image.width = size;
    image.height = size;
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load SVG icon'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
  });
}

// ===== PUBLIC API =====

/**
 * Task sprite super-sampling factor — every task sprite (border + composite)
 * is rendered at NxN times the logical 64-px size and registered with
 * `pixelRatio = N`. With ~45 task sprites we use a moderate 2× to cap atlas
 * memory; the note composite (single sprite) uses a higher 4× factor.
 */
const TASK_SPRITE_PIXEL_RATIO = 2;

/** Note composite super-sampling factor — single sprite, can afford 4×. */
const NOTE_COMPOSITE_PIXEL_RATIO = 4;

/** Generates a plain shape sprite (used for border layer). */
export function generateShapeSprite(
  shape: ShapeType,
  size: number = SHAPE_SPRITE_SIZE
): ImageData {
  const { ctx } = createCanvas(size, size);
  SHAPE_DRAW_FUNCTIONS[shape](ctx, size);
  return extractImageData(ctx, size);
}

/**
 * Generates a composite SDF sprite: shape fill with icon cutout.
 * The cutout area becomes transparent, revealing the border layer underneath.
 * For shapes without an icon, returns a solid shape (no cutout).
 *
 * `pixelRatio` super-samples the canvas; the caller must register the sprite
 * with the same `pixelRatio` so MapLibre treats it as the same logical size.
 */
function generateCompositeSprite(
  shape: ShapeType,
  iconImage?: HTMLImageElement,
  pixelRatio: number = 1
): ImageData {
  const size = SHAPE_SPRITE_SIZE * pixelRatio;
  const { ctx } = createCanvas(size, size);

  // 1. Draw shape fill
  SHAPE_DRAW_FUNCTIONS[shape](ctx, size);

  // 2. If icon: cut it out using destination-out
  if (iconImage) {
    ctx.globalCompositeOperation = 'destination-out';

    const iconSize = COMPOSITE_ICON_SIZE * pixelRatio;
    const offsetX = (size - iconSize) / 2;
    let offsetY = (size - iconSize) / 2;

    // Shift icon down for triangles (visual centroid is lower than center)
    if (shape === 'triangle') {
      offsetY += TRIANGLE_ICON_OFFSET_Y * pixelRatio;
    }

    ctx.drawImage(iconImage, offsetX, offsetY, iconSize, iconSize);
    ctx.globalCompositeOperation = 'source-over';
  }

  return extractImageData(ctx, size);
}

/**
 * Generates the solid note-pin sprite: teardrop pin shape with no icon cutout.
 * Rendered as the bottom layer in the per-feature pin color. `pixelRatio`
 * super-samples the canvas; the caller must register the sprite with the same
 * `pixelRatio` so MapLibre treats it as a logical SHAPE_SPRITE_SIZE sprite.
 */
function generateNotePinSprite(pixelRatio: number = 1): ImageData {
  const size = SHAPE_SPRITE_SIZE * pixelRatio;
  const { ctx } = createCanvas(size, size);
  drawTeardropPin(ctx, size);
  return extractImageData(ctx, size);
}

/**
 * Generates the note-icon sprite: only the IconNote glyph, drawn solid (no
 * cutout) on a transparent canvas, positioned at the teardrop head center so
 * it aligns with the pin sprite when both layers share `icon-anchor: 'bottom'`.
 */
function generateNoteIconSprite(
  iconImage: HTMLImageElement,
  pixelRatio: number = 1
): ImageData {
  const size = SHAPE_SPRITE_SIZE * pixelRatio;
  const { ctx } = createCanvas(size, size);

  const iconSize = NOTE_COMPOSITE_ICON_SIZE * pixelRatio;
  const headCenterY = size / 2;
  const offsetX = (size - iconSize) / 2;
  const offsetY = headCenterY - iconSize / 2;

  ctx.drawImage(iconImage, offsetX, offsetY, iconSize, iconSize);

  return extractImageData(ctx, size);
}

// ===== SPRITE ENTRY =====

export interface SpriteEntry {
  data: ImageData;
  sdf: boolean;
  /** Source-pixel-to-CSS-pixel ratio. Defaults to 1; use higher values for
   *  super-sampled sprites so MapLibre keeps the same logical icon size. */
  pixelRatio?: number;
}

// ===== SPRITE KEY HELPERS =====

/** Marker shape types used by Task layer (clusterable, multi-icon) */
export const SHAPE_TYPES: ReadonlyArray<ShapeType> = [
  'circle',
  'square',
  'triangle',
];

/** Sprite key for the solid teardrop pin (rendered in the per-feature color). */
export const NOTE_PIN_SPRITE_KEY = 'note-pin-solid';

/** Sprite key for the IconNote glyph rendered on top of the pin. */
export const NOTE_ICON_SPRITE_KEY = 'note-pin-icon';

/** Builds the composite sprite key used for MapLibre image registration */
export function compositeKey(shape: string, icon?: string): string {
  return icon ? `composite-${shape}-${icon}` : `composite-${shape}`;
}

// ===== GENERATE ALL =====

/**
 * Generates all SDF sprites:
 * - 3 plain shape sprites for task border layer (shape-circle, shape-square, shape-triangle)
 * - 42 composite sprites for task marker layer (3 shapes × 14 variants: 13 icons + no-icon)
 * - 1 solid pin sprite + 1 icon glyph sprite for note marker layer
 *
 * Icon SVGs are pre-loaded once (13 task icons + 1 note icon) and reused.
 */
export async function generateAllSprites(): Promise<Map<string, SpriteEntry>> {
  const sprites = new Map<string, SpriteEntry>();
  const iconKeys = Object.values(MarkerIcon);

  // Task border layer — plain shape sprites at TASK_SPRITE_PIXEL_RATIO super-sampling
  const taskShapeRenderSize = SHAPE_SPRITE_SIZE * TASK_SPRITE_PIXEL_RATIO;
  for (const shape of SHAPE_TYPES) {
    const data = generateShapeSprite(shape, taskShapeRenderSize);
    sprites.set(`shape-${shape}`, {
      data,
      sdf: true,
      pixelRatio: TASK_SPRITE_PIXEL_RATIO,
    });
  }

  // Pre-load all task icons + the note icon once (async, parallel)
  const taskIconRasterSize = COMPOSITE_ICON_SIZE * TASK_SPRITE_PIXEL_RATIO;
  const iconImages = new Map<MarkerIconType, HTMLImageElement>();
  const taskIconLoadPromises = iconKeys.map(async (iconKey) => {
    const svgMarkup = getIconSvgMarkup(iconKey, taskIconRasterSize);
    const image = await loadSvgAsImage(svgMarkup, taskIconRasterSize);
    iconImages.set(iconKey, image);
  });
  const noteIconRasterSize =
    NOTE_COMPOSITE_ICON_SIZE * NOTE_COMPOSITE_PIXEL_RATIO;
  const noteIconPromise = loadSvgAsImage(
    getNoteIconSvgMarkup(noteIconRasterSize),
    noteIconRasterSize
  );

  const [, noteIconImage] = await Promise.all([
    Promise.all(taskIconLoadPromises),
    noteIconPromise,
  ]);

  // Generate task composite sprites (synchronous — icons already loaded)
  for (const shape of SHAPE_TYPES) {
    // No-icon variant
    const noIconData = generateCompositeSprite(
      shape,
      undefined,
      TASK_SPRITE_PIXEL_RATIO
    );
    sprites.set(compositeKey(shape), {
      data: noIconData,
      sdf: true,
      pixelRatio: TASK_SPRITE_PIXEL_RATIO,
    });

    // With-icon variants
    for (const iconKey of iconKeys) {
      const iconImage = iconImages.get(iconKey);
      if (!iconImage) continue;
      const data = generateCompositeSprite(
        shape,
        iconImage,
        TASK_SPRITE_PIXEL_RATIO
      );
      sprites.set(compositeKey(shape, iconKey), {
        data,
        sdf: true,
        pixelRatio: TASK_SPRITE_PIXEL_RATIO,
      });
    }
  }

  // Note marker — solid teardrop pin (per-feature color) + separate IconNote
  // glyph (tinted with a contrast color by the layer paint expression).
  sprites.set(NOTE_PIN_SPRITE_KEY, {
    data: generateNotePinSprite(NOTE_COMPOSITE_PIXEL_RATIO),
    sdf: true,
    pixelRatio: NOTE_COMPOSITE_PIXEL_RATIO,
  });
  sprites.set(NOTE_ICON_SPRITE_KEY, {
    data: generateNoteIconSprite(noteIconImage, NOTE_COMPOSITE_PIXEL_RATIO),
    sdf: true,
    pixelRatio: NOTE_COMPOSITE_PIXEL_RATIO,
  });

  // Obstruction anchor sprites — 1 composite disc per ObstructionType. Lives
  // in `obstructionSprites.ts` to keep the per-feature icon set self-contained;
  // we merge into the shared atlas here so a single `getSpriteCache()` call
  // covers every symbol layer.
  const obstructionSprites = await generateObstructionSprites();
  for (const [key, entry] of obstructionSprites) {
    sprites.set(key, entry);
  }

  return sprites;
}
